import { ModerationSettings } from '@tribeplatform/gql-client/types'
import { useAdminOnboarding } from '@tribeplatform/react-components/AdminOnboarding'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useUpdateModerationSettings } from '@tribeplatform/react-sdk/hooks'
import { Badge } from '@tribeplatform/react-ui-kit/Badge'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

interface Props {
  moderationSettings: ModerationSettings
}

export const AISpamDetector = ({ moderationSettings }: Props) => {
  const { akismet, oopSpam } = moderationSettings

  const { $t } = useI18n()
  const { mutateAsync: updateModerationSettings, isLoading } =
    useUpdateModerationSettings()
  const { completeStep } = useAdminOnboarding()

  const submit = data => {
    const { akismet, oopSpam } = data
    updateModerationSettings({
      input: {
        akismet,
        oopSpam,
      },
    })
      .then(() => {
        completeStep({
          step: 'moderation-rules',
        })
        toast({
          title: $t({
            defaultMessage: 'Moderation settings updated.',
            id: 'Admin.ModerationSettings.Updated',
          }),
          status: 'success',
        })
      })
      .catch(() => {
        toast({
          title: $t({
            defaultMessage: 'Unable to update moderation settings.',
            id: 'Admin.ModerationSettings.FailedToUpdate',
          }),
          status: 'error',
        })
      })
  }

  return (
    <Form
      defaultValues={{
        akismet,
        oopSpam,
      }}
      onSubmit={submit}
    >
      {({ formState: { isDirty } }) => (
        <Card>
          <Card.Header
            title={
              <div className="flex gap-3">
                <p>
                  <T
                    defaultMessage="AI spam detector"
                    id="Admin.ModerationSettings.AISpamDetector"
                  />
                </p>
                <div className="flex flex-col justify-center">
                  <Badge variant="positive">Beta</Badge>
                </div>
              </div>
            }
            description={$t({
              defaultMessage:
                'Enable AI spam detector to automatically detect spam posts and comments.',
              id: 'Admin.ModerationSettings.AISpamDetectorDescription',
            })}
          />
          <Card.Content>
            <div className="web:col-span-12 web:md:col-span-8 flex flex-col space-y-5">
              <Form.Toggle
                name="akismet"
                label={$t({
                  defaultMessage: 'Akismet',
                  id: 'Admin.ModerationSettings.Akismet',
                })}
                helperText={
                  <T
                    defaultMessage={`Enable Akismet AI to automatically detect spam posts and comments. User's data including IP address, user agent, referrer, and site URL will be sent to <link>Akismet</link> for spam checking.`}
                    id="Admin.ModerationSettings.AkismetHelperText"
                    values={{
                      link: chunks => (
                        <Link
                          className="font-bold"
                          href="https://akismet.com/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {chunks}
                        </Link>
                      ),
                    }}
                  />
                }
              />
              <Form.Toggle
                name="oopSpam"
                label={$t({
                  defaultMessage: 'OOPSpam',
                  id: 'Admin.ModerationSettings.OOPSpam',
                })}
                helperText={
                  <T
                    defaultMessage={`Enable OOPSpam to automatically detect spam posts and comments. User's data including IP address, user agent, referrer, and site URL will be sent to <link>OOPSpam</link> for spam checking.`}
                    id="Admin.ModerationSettings.OOPSpamHelperText"
                    values={{
                      link: chunks => (
                        <Link
                          className="font-bold"
                          href="https://www.oopspam.com/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {chunks}
                        </Link>
                      ),
                    }}
                  />
                }
              />
            </div>
          </Card.Content>
          <Card.Actions>
            <Button type="submit" loading={isLoading} disabled={!isDirty}>
              <T defaultMessage="Update" id="Generics.Update" />
            </Button>
          </Card.Actions>
        </Card>
      )}
    </Form>
  )
}
